<template>
  <div class="user-management">
    <!-- 页面标题 -->
    <div class="page-header mb-4">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h1 class="page-title">
            <i class="bi bi-people me-2"></i>
            用户管理
          </h1>
          <p class="page-subtitle text-muted mb-0">
            管理系统中的所有用户账户和权限
          </p>
        </div>
        <div class="header-actions">
          <button class="btn btn-primary me-2" @click="showCreateModal = true">
            <i class="bi bi-plus-lg me-1"></i>
            创建用户
          </button>
          <button class="btn btn-secondary" @click="showMergeModal = true">
            <i class="bi bi-people me-1"></i>
            合并用户
          </button>
        </div>
      </div>
    </div>

    <!-- 搜索和筛选 -->
    <UserStatisticsFilters
      v-model:search-query="searchQuery"
      v-model:role-filter="roleFilter"
      v-model:status-filter="statusFilter"
      v-model:date-field="dateField"
      v-model:start-date="startDate"
      v-model:end-date="endDate"
      :role-options="roleOptions"
      class="mb-4"
      @reset="resetFilters"
    />

    <!-- 用户列表 -->
    <div class="users-card">
      <div class="table-responsive">
        <table class="table table-hover">
          <thead class="table-light">
            <tr>
              <th>ID</th>
              <th>用户名</th>
              <th class="col-name">姓名</th>
              <th>角色</th>
              <th>状态</th>
              <th>
                <button type="button" class="sort-button" @click="toggleSort('totalQuestions')">
                  刷题数 <span aria-hidden="true">{{ sortIndicator('totalQuestions') }}</span>
                </button>
              </th>
              <th>正确率</th>
              <th>
                <button type="button" class="sort-button" @click="toggleSort('wrongQuestionCount')">
                  错题数 <span aria-hidden="true">{{ sortIndicator('wrongQuestionCount') }}</span>
                </button>
              </th>
              <th>
                <button type="button" class="sort-button" @click="toggleSort('lastActiveTime')">
                  最后活跃 <span aria-hidden="true">{{ sortIndicator('lastActiveTime') }}</span>
                </button>
              </th>
              <th class="col-actions">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in paginatedUsers" :key="user.id">
              <td>{{ user.id }}</td>
              <td>
                <div class="d-flex align-items-center">
                  <div class="user-avatar me-2">
                    <img v-if="user.avatar" :src="user.avatar" :alt="user.username" class="avatar-img">
                    <div v-else class="avatar-placeholder">
                      {{ user.username.charAt(0).toUpperCase() }}
                    </div>
                  </div>
                  <span class="fw-medium">{{ user.username }}</span>
                </div>
              </td>
              <td class="col-name">{{ user.firstName || '' }} {{ user.lastName || '' }}</td>
              <td>
                <span class="badge" :class="getRoleBadgeClass(user.role)">
                  {{ getRoleDisplayName(user.role) }}
                </span>
              </td>
              <td>
                <span class="badge" :class="user.isActive ? 'bg-success' : 'bg-danger'">
                  {{ user.isActive ? '启用' : '禁用' }}
                </span>
              </td>
              <td>
                <span class="fw-medium text-primary">
                  {{ user.activityStats?.totalQuestions || 0 }}
                </span>
              </td>
              <td>
                <span class="badge" :class="getCorrectRateBadgeClass(user.activityStats?.correctRate || 0)">
                  {{ user.activityStats?.correctRate || 0 }}%
                </span>
              </td>
              <td>
                <span class="text-danger fw-medium">
                  {{ user.activityStats?.wrongQuestionCount || 0 }}
                </span>
              </td>
              <td>{{ formatLastActiveTime(user.activityStats?.lastActiveTime) }}</td>
              <td class="col-actions">
                <div class="btn-group-text">
                  <button class="btn-text text-primary" @click="editUser(user)" title="编辑">
                    编辑
                  </button>
                  <button 
                    class="btn-text" 
                    :class="user.isActive ? 'text-warning' : 'text-success'"
                    @click="toggleUserStatus(user)"
                    :title="user.isActive ? '禁用' : '启用'"
                  >
                    {{ user.isActive ? '禁用' : '启用' }}
                  </button>
                  <button 
                    class="btn-text text-danger" 
                    @click="deleteUser(user)" 
                    title="删除"
                    :disabled="isAdminRole(user.role)"
                  >
                    删除
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        
        <!-- 空状态 -->
        <div v-if="filteredUsers.length === 0" class="text-center py-5">
          <i class="bi bi-people display-1 text-muted mb-3"></i>
          <h5 class="text-muted">暂无用户数据</h5>
          <p class="text-muted">{{ searchQuery ? '没有找到匹配的用户' : '系统中还没有用户' }}</p>
        </div>
      </div>
      
      <!-- 分页 -->
      <div class="d-flex justify-content-between align-items-center mt-3">
          <div class="text-muted">
            显示 {{ filteredUsers.length > 0 ? (currentPage - 1) * pageSize + 1 : 0 }} - {{ Math.min(currentPage * pageSize, filteredUsers.length) }} 条，共 {{ filteredUsers.length }} 条
          </div>
        <nav v-if="totalPages > 1">
            <ul class="pagination pagination-sm mb-0">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <button class="page-link" @click="goToPage(1)" :disabled="currentPage === 1" title="首页">
                <span aria-hidden="true">&laquo;</span>
              </button>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <button class="page-link" @click="goToPage(currentPage - 1)" :disabled="currentPage === 1" title="上一页">
                <span aria-hidden="true">&lsaquo;</span>
              </button>
            </li>
            <li 
              class="page-item" 
              v-for="(page, index) in visiblePages" 
              :key="index"
              :class="{ active: page === currentPage, disabled: page === '...' }"
            >
              <span v-if="page === '...'" class="page-link el-pagination-ellipsis">
                <span aria-hidden="true">...</span>
              </span>
              <button v-else class="page-link" @click="goToPage(Number(page))">{{ page }}</button>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
              <button class="page-link" @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages" title="下一页">
                <span aria-hidden="true">&rsaquo;</span>
              </button>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
              <button class="page-link" @click="goToPage(totalPages)" :disabled="currentPage === totalPages" title="尾页">
                <span aria-hidden="true">&raquo;</span>
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <!-- 创建/编辑用户模态框 -->
    <div class="modal fade" :class="{ show: showCreateModal || showEditModal }" :style="{ display: showCreateModal || showEditModal ? 'block' : 'none' }" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-person-plus me-2"></i>
              {{ showCreateModal ? '创建用户' : '编辑用户' }}
            </h5>
            <button type="button" class="btn-close" @click="closeModal"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveUser">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">用户名 <span class="text-danger">*</span></label>
                  <input 
                    type="text" 
                    class="form-control" 
                    v-model="userForm.username" 
                    :disabled="showEditModal"
                    required
                  >
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">邮箱</label>
                  <input type="email" class="form-control" v-model="userForm.email">
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">姓</label>
                  <input type="text" class="form-control" v-model="userForm.firstName">
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">名</label>
                  <input type="text" class="form-control" v-model="userForm.lastName">
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">手机号</label>
                  <input type="tel" class="form-control" v-model="userForm.phone">
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">角色 <span class="text-danger">*</span></label>
                  <select class="form-select" v-model="userForm.role" required>
                    <option v-for="role in roleOptions" :key="role.id" :value="role.name">
                      {{ getRoleDisplayName(role.name) }}
                    </option>
                  </select>
                </div>
                <div class="col-md-6 mb-3" v-if="showCreateModal">
                  <label class="form-label">密码 <span class="text-danger">*</span></label>
                  <input type="password" class="form-control" v-model="userForm.password" required>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">状态</label>
                  <div class="form-check form-switch">
                    <input class="form-check-input" type="checkbox" v-model="userForm.isActive">
                    <label class="form-check-label">
                      {{ userForm.isActive ? '启用' : '禁用' }}
                    </label>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">取消</button>
            <button type="button" class="btn btn-primary" @click="saveUser" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
              {{ showCreateModal ? '创建' : '保存' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 模态框背景 -->
    <div class="modal-backdrop fade" :class="{ show: showCreateModal || showEditModal }" v-if="showCreateModal || showEditModal"></div>

    <!-- 合并用户对话框 -->
    <MergeUsersDialog
      v-if="showMergeModal"
      :show="showMergeModal"
      :users="users"
      @close="showMergeModal = false"
      @success="handleMergeSuccess"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useAuthStore } from '../../stores/auth'
import { usersApi, type Role } from '../../api/users'
import MergeUsersDialog from '../../components/admin/MergeUsersDialog.vue'
import UserStatisticsFilters from '../../components/admin/UserStatisticsFilters.vue'
import type { UserStatisticsFilterState, UserSortField, UserWithActivity } from '../../types/user-statistics'
import { filterAndSortUsers } from '../../utils/user-statistics'

interface UserForm {
  username: string
  email: string
  firstName: string
  lastName: string
  phone: string
  role: string
  password: string
  isActive: boolean
}

const authStore = useAuthStore()

// 响应式数据
const users = ref<UserWithActivity[]>([])
const searchQuery = ref('')
const roleFilter = ref('')
const statusFilter = ref<UserStatisticsFilterState['statusFilter']>('')
const dateField = ref<UserStatisticsFilterState['dateField']>('createdAt')
const startDate = ref('')
const endDate = ref('')
const sortBy = ref<UserSortField>('')
const sortOrder = ref<UserStatisticsFilterState['sortOrder']>('desc')
const currentPage = ref(1)
const pageSize = ref(20)
const totalUsers = ref(0)
const roleOptions = ref<Role[]>([])
const loading = ref(false)
const saving = ref(false)

watch([searchQuery, roleFilter, statusFilter, dateField, startDate, endDate], () => {
  currentPage.value = 1
})

// 模态框状态
const showCreateModal = ref(false)
const showEditModal = ref(false)
const showMergeModal = ref(false)
const editingUser = ref<UserWithActivity | null>(null)

// 用户表单
const userForm = ref<UserForm>({
  username: '',
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  role: 'user',
  password: '',
  isActive: true
})

const getRoleName = (role: any) => {
  if (typeof role === 'object' && role) {
    return role.name || ''
  }
  return role || ''
}

const isAdminRole = (role: any) => getRoleName(role) === 'admin'

const getRoleIdByName = (roleName: string) => {
  const matchedRole = roleOptions.value.find(role => role.name === roleName)
  if (matchedRole) {
    return matchedRole.id
  }

  const fromUsers = users.value
    .map(user => user.role)
    .find((role): role is { id?: number; name?: string } => (
      typeof role === 'object' && role !== null && role.name === roleName && Boolean(role.id)
    ))

  return fromUsers?.id
}

// 计算属性
const filteredUsers = computed(() => {
  return filterAndSortUsers(users.value, {
    searchQuery: searchQuery.value,
    roleFilter: roleFilter.value,
    statusFilter: statusFilter.value,
    dateField: dateField.value,
    startDate: startDate.value,
    endDate: endDate.value,
    sortBy: sortBy.value,
    sortOrder: sortOrder.value,
  }, getRoleName)
})

// 分页显示的用户列表
const paginatedUsers = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  const end = start + pageSize.value
  return filteredUsers.value.slice(start, end)
})

const totalPages = computed(() => Math.ceil(filteredUsers.value.length / pageSize.value))

const visiblePages = computed(() => {
  const pages: (number | string)[] = []
  const current = currentPage.value
  const total = totalPages.value
  const maxPages = 7 // 最大显示页码数

  if (total <= maxPages) {
    // 总页数较少时，显示所有页码
    for (let i = 1; i <= total; i++) {
      pages.push(i)
    }
  } else {
    // 总页数较多时，使用折叠逻辑
    if (current < 5) {
      // 当前页靠前：1 2 3 4 5 6 ... total
      for (let i = 1; i <= 6; i++) pages.push(i)
      pages.push('...')
      pages.push(total)
    } else if (current > total - 4) {
      // 当前页靠后：1 ... total-5 total-4 total-3 total-2 total-1 total
      pages.push(1)
      pages.push('...')
      for (let i = total - 5; i <= total; i++) pages.push(i)
    } else {
      // 当前页居中：1 ... current-2 current-1 current current+1 current+2 ... total
      pages.push(1)
      pages.push('...')
      for (let i = current - 2; i <= current + 2; i++) pages.push(i)
      pages.push('...')
      pages.push(total)
    }
  }
  
  return pages
})

// 方法
const loadUsers = async () => {
  loading.value = true
  try {
    const [usersData, rolesData] = await Promise.all([
      usersApi.getUsersWithActivityStats(),
      usersApi.getRoles()
    ])
    roleOptions.value = rolesData
    users.value = usersData.map(user => ({
      id: user.id,
      username: user.username,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email,
      phone: user.phone,
      role: user.role,
      isActive: user.isActive,
      lastLoginAt: user.activityStats?.lastActiveTime ? new Date(user.activityStats.lastActiveTime) : null,
      createdAt: new Date(user.createdAt),
      activityStats: user.activityStats || {
        totalQuestions: 0,
        correctAnswers: 0,
        correctRate: 0,
        wrongQuestionCount: 0,
        masteredWrongQuestions: 0,
        totalSessions: 0,
        averageScore: 0,
        lastActiveTime: null
      }
    }))
    if (roleOptions.value.length > 0 && !roleOptions.value.some(role => role.name === userForm.value.role)) {
      userForm.value.role = roleOptions.value[0].name
    }
    totalUsers.value = users.value.length
  } catch (error) {
    console.error('加载用户列表失败:', error)
  } finally {
    loading.value = false
  }
}

const resetFilters = () => {
  searchQuery.value = ''
  roleFilter.value = ''
  statusFilter.value = ''
  dateField.value = 'createdAt'
  startDate.value = ''
  endDate.value = ''
  sortBy.value = ''
  sortOrder.value = 'desc'
  currentPage.value = 1
}

const toggleSort = (field: Exclude<UserSortField, ''>) => {
  sortOrder.value = sortBy.value === field && sortOrder.value === 'desc' ? 'asc' : 'desc'
  sortBy.value = field
  currentPage.value = 1
}

const sortIndicator = (field: Exclude<UserSortField, ''>) => {
  if (sortBy.value !== field) return '↕'
  return sortOrder.value === 'desc' ? '↓' : '↑'
}

const handleMergeSuccess = async () => {
  // 重新加载用户列表以获取最新数据
  await loadUsers()
  
  // 显示成功提示
  alert('用户合并成功！')
}

const goToPage = (page: number) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

const getRoleBadgeClass = (role: any) => {
  // 如果role是对象，取name属性；如果是字符串，直接使用
  const roleName = typeof role === 'object' && role ? role.name : role
  switch (roleName) {
    case 'admin':
      return 'bg-danger'
    case 'user':
      return 'bg-primary'
    default:
      return 'bg-secondary'
  }
}

const getRoleDisplayName = (role: any) => {
  // 如果role是对象，取name属性；如果是字符串，直接使用
  const roleName = typeof role === 'object' && role ? role.name : role
  switch (roleName) {
    case 'admin':
      return '管理员'
    case 'user':
      return '普通用户'
    default:
      return roleName || '未知角色'
  }
}

const formatDate = (date: Date | string | null | undefined) => {
   if (!date) return '-'
   const d = new Date(date)
   return d.toLocaleDateString('zh-CN') + ' ' + d.toLocaleTimeString('zh-CN', { hour12: false })
 }

const getCorrectRateBadgeClass = (correctRate: number) => {
  if (correctRate >= 80) {
    return 'bg-success'
  } else if (correctRate >= 60) {
    return 'bg-warning'
  } else {
    return 'bg-danger'
  }
}

const formatLastActiveTime = (lastActiveTime: string | Date | null | undefined) => {
  if (!lastActiveTime) return '-'
  
  const now = new Date()
  const activeTime = new Date(lastActiveTime)
  const diffMs = now.getTime() - activeTime.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  
  if (diffDays === 0) {
    return '今天'
  } else if (diffDays === 1) {
    return '昨天'
  } else if (diffDays < 7) {
    return `${diffDays}天前`
  } else {
    return activeTime.toLocaleDateString('zh-CN')
  }
}

// 截断邮箱显示
const truncateEmail = (email: string) => {
  if (!email) return ''
  if (email.length <= 18) return email
  
  const atIndex = email.indexOf('@')
  if (atIndex === -1) {
    return email.substring(0, 15) + '...'
  }
  
  const localPart = email.substring(0, atIndex)
  const domainPart = email.substring(atIndex)
  
  if (localPart.length > 10) {
    return localPart.substring(0, 8) + '...' + domainPart
  }
  
  return email
}

const editUser = (user: any) => {
  editingUser.value = user
  userForm.value = {
    username: user.username,
    email: user.email || '',
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    phone: user.phone || '',
    role: getRoleName(user.role) || 'user',
    password: '',
    isActive: user.isActive
  }
  showEditModal.value = true
}

const toggleUserStatus = async (user: any) => {
  try {
    await usersApi.toggleUserStatus(user.id, !user.isActive)
    user.isActive = !user.isActive
  } catch (error) {
    console.error('切换用户状态失败:', error)
  }
}

const deleteUser = async (user: any) => {
  if (confirm(`确定要删除用户 "${user.username}" 吗？此操作不可撤销。`)) {
    try {
      await usersApi.deleteUser(user.id)
      const index = users.value.findIndex(u => u.id === user.id)
      if (index > -1) {
        users.value.splice(index, 1)
        totalUsers.value--
      }
    } catch (error) {
      console.error('删除用户失败:', error)
    }
  }
}

const saveUser = async () => {
  saving.value = true
  try {
    const selectedRoleId = getRoleIdByName(userForm.value.role)
    if (!selectedRoleId) {
      alert('所选角色不存在，请刷新页面后重试')
      return
    }

    if (showCreateModal.value) {
      // 创建新用户
      const createData = {
        username: userForm.value.username,
        password: userForm.value.password,
        firstName: userForm.value.firstName,
        lastName: userForm.value.lastName,
        email: userForm.value.email,
        phone: userForm.value.phone,
        roleId: selectedRoleId,
        isActive: userForm.value.isActive
      }
      await usersApi.createUser(createData)
      await loadUsers() // 重新加载用户列表
    } else {
       // 更新用户
       if (editingUser.value) {
         const updateData = {
           username: userForm.value.username,
           firstName: userForm.value.firstName,
           lastName: userForm.value.lastName,
           email: userForm.value.email,
           phone: userForm.value.phone,
           roleId: selectedRoleId,
           isActive: userForm.value.isActive
         }
         await usersApi.updateUser(editingUser.value.id, updateData)
         await loadUsers() // 重新加载用户列表
       }
     }
    
    closeModal()
  } catch (error) {
    console.error('保存用户失败:', error)
  } finally {
    saving.value = false
  }
}

const closeModal = () => {
  showCreateModal.value = false
  showEditModal.value = false
  editingUser.value = null
  userForm.value = {
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: roleOptions.value.find(role => role.name === 'user')?.name || roleOptions.value[0]?.name || 'user',
    password: '',
    isActive: true
  }
}

onMounted(() => {
  loadUsers()
})
</script>

<style scoped>
.user-management {
  padding: 1.5rem;
  background-color: #ECFEFF; /* 从建议中获取的背景色 */
  min-height: 100vh;
}

.page-header {
  background: white;
  color: #164E63; /* 深青色文本 */
  padding: 1.5rem 2rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  border-left: 4px solid #0891B2; /* 左侧高亮边框 */
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
  color: #164E63;
}

.page-subtitle {
  font-size: 0.95rem;
  color: #475569;
}

.header-actions .btn {
  font-weight: 500;
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.header-actions .btn-primary {
  background-color: #0891B2;
  border-color: #0891B2;
}

.header-actions .btn-primary:hover {
  background-color: #0E7490;
  border-color: #0E7490;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(8, 145, 178, 0.3);
}

.header-actions .btn-secondary {
  background-color: white;
  color: #0891B2;
  border: 1px solid #0891B2;
}

.header-actions .btn-secondary:hover {
  background-color: #ECFEFF;
  transform: translateY(-1px);
}

.users-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  border: 1px solid #E2E8F0;
}

.table {
  margin-bottom: 0;
  font-size: 0.95rem;
}

.table th {
  border-top: none;
  border-bottom: 2px solid #E2E8F0;
  font-weight: 600;
  color: #475569;
  padding: 1rem 0.75rem;
  text-transform: uppercase;
  font-size: 0.85rem;
  letter-spacing: 0.05em;
  white-space: nowrap;
}

.sort-button {
  padding: 0;
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  white-space: nowrap;
  background: transparent;
  border: 0;
}

.sort-button:hover,
.sort-button:focus-visible {
  color: #0891B2;
}

.table td {
  padding: 1rem 0.75rem;
  vertical-align: middle;
  border-bottom: 1px solid #F1F5F9;
  color: #334155;
}

.table-hover tbody tr {
  transition: background-color 0.15s ease;
}

.table-hover tbody tr:hover {
  background-color: #F8FAFC;
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid #ECFEFF;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #0891B2, #22D3EE);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1rem;
}

.email-display {
  cursor: pointer;
  color: #64748B;
  transition: color 0.2s;
}

.email-display:hover {
  color: #0891B2;
}

.badge {
  font-size: 0.75rem;
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  font-weight: 500;
  letter-spacing: 0.025em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none; /* 移除边框 */
}

.bg-success { 
  background-color: #ECFDF5 !important; 
  color: #10B981 !important; 
}
.bg-danger { 
  background-color: #FEF2F2 !important; 
  color: #EF4444 !important; 
}
.bg-warning { 
  background-color: #FFFBEB !important; 
  color: #F59E0B !important; 
}
.bg-primary { 
  background-color: #ECFEFF !important; 
  color: #0891B2 !important; 
}
.bg-secondary { 
  background-color: #F8FAFC !important; 
  color: #64748B !important; 
}

.btn-group-sm .btn {
  padding: 0.35rem;
  font-size: 1rem;
  width: 2.2rem;
  height: 2.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50% !important; /* 强制圆形按钮，区别于状态标签 */
  margin: 0 0.25rem;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  background-color: white;
  border: 1px solid #E2E8F0;
  color: #64748B;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.btn-group-sm .btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.btn-outline-primary:hover:not(:disabled) {
  background-color: #0891B2;
  color: white;
  border-color: #0891B2;
}

.btn-outline-warning:hover:not(:disabled) {
  background-color: #F59E0B;
  color: white;
  border-color: #F59E0B;
}

.btn-outline-success:hover:not(:disabled) {
  background-color: #10B981;
  color: white;
  border-color: #10B981;
}

.btn-outline-danger:hover:not(:disabled) {
  background-color: #EF4444;
  color: white;
  border-color: #EF4444;
}

.btn-group-sm .btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #F8FAFC;
}

.btn-group-sm .btn i {
  font-size: 1rem;
}

/* 分页样式优化 - 参考 Element Plus 风格 */
.pagination {
  gap: 0.25rem;
}

.pagination .page-link {
  color: #475569;
  border: none; /* 移除默认边框 */
  background-color: #F8FAFC; /* 默认灰色背景 */
  margin: 0;
  border-radius: 4px; /* 更小的圆角 */
  min-width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  transition: all 0.2s;
}

.pagination .page-item.active .page-link {
  background-color: #0EA5E9; /* 鲜明的天蓝色激活态 */
  color: white;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(14, 165, 233, 0.2);
}

.pagination .page-link:hover:not(:disabled):not(.el-pagination-ellipsis) {
  color: #0EA5E9;
  background-color: #E0F2FE; /* 浅蓝悬停背景 */
}

.pagination .page-item.disabled .page-link {
  background-color: #F1F5F9;
  color: #CBD5E1;
  cursor: not-allowed;
}

.pagination .el-pagination-ellipsis {
  background-color: transparent !important;
  color: #94A3B8;
  cursor: default;
}

.table-responsive {
  position: relative;
}

.table th.col-actions,
.table td.col-actions {
  position: sticky;
  right: 0;
  background-color: white; /* 固定列的背景色必须是实色 */
  z-index: 1;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.05); /* 左侧阴影表示浮动层 */
  width: 180px; /* 增加操作列宽度以容纳三个按钮 */
  min-width: 180px;
  text-align: center;
  white-space: nowrap; /* 强制不换行 */
}

.table th.col-name,
.table td.col-name {
  min-width: 100px; /* 减小姓名列宽度 */
}

.table td {
  white-space: nowrap; /* 整个表格单元格默认不换行，防止挤压 */
}

.table-hover tbody tr:hover td.col-actions {
  background-color: #F8FAFC; /* 悬停时跟随行背景色 */
}

/* 文字按钮样式 */
.btn-group-text {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

.btn-text {
  background: white;
  border: 1px solid #E2E8F0;
  padding: 0.35rem 0.6rem;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.btn-text:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.btn-text:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #F8FAFC;
}

.btn-text.text-primary { 
  color: #0891B2; 
  border-color: #A5F3FC;
  background-color: #ECFEFF; /* 默认浅底色 */
}
.btn-text.text-primary:hover:not(:disabled) { 
  background-color: #CFFAFE; /* 悬停稍微加深 */
  border-color: #0891B2;
}

.btn-text.text-warning { 
  color: #F59E0B; 
  border-color: #FDE68A;
  background-color: #FFFBEB;
}
.btn-text.text-warning:hover:not(:disabled) { 
  background-color: #FEF3C7;
  border-color: #F59E0B;
}

.btn-text.text-success { 
  color: #10B981; 
  border-color: #A7F3D0;
  background-color: #ECFDF5;
}
.btn-text.text-success:hover:not(:disabled) { 
  background-color: #D1FAE5;
  border-color: #10B981;
}

.btn-text.text-danger { 
  color: #EF4444; 
  border-color: #FECACA;
  background-color: #FEF2F2;
}
.btn-text.text-danger:hover:not(:disabled) { 
  background-color: #FEE2E2;
  border-color: #EF4444;
}

.modal.show {
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
}

.modal-content {
  border-radius: 16px;
  border: none;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.modal-header {
  border-bottom: 1px solid #E0F2FE;
  background-color: #F8FAFC;
  border-radius: 16px 16px 0 0;
  padding: 1.25rem 1.5rem;
}

.modal-title {
  color: #0C4A6E;
  font-weight: 600;
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  border-top: 1px solid #E0F2FE;
  padding: 1.25rem 1.5rem;
}

.modal-backdrop.show {
  opacity: 0; /* 使用 modal 自身的背景色 */
}

/* 表单内元素优化 */
.form-label {
  font-weight: 500;
  color: #475569;
  margin-bottom: 0.5rem;
}

.modal-body .form-control,
.modal-body .form-select {
  border-radius: 8px;
  border: 1px solid #BAE6FD;
  padding: 0.6rem 1rem;
  transition: all 0.2s;
}

.modal-body .form-control:focus,
.modal-body .form-select:focus {
  border-color: #0EA5E9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .user-management {
    padding: 1rem;
  }
  
  .page-header {
    padding: 1.25rem;
  }
  
  .page-title {
    font-size: 1.5rem;
  }
  
  .users-card {
    padding: 1.25rem;
  }
  
  .table-responsive {
    font-size: 0.875rem;
  }
  
  .btn-group-sm .btn {
    padding: 0.35rem 0.55rem;
    min-width: 2.2rem;
    min-height: 2rem;
    margin: 0 0.1rem;
  }
  
  .header-actions {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 1rem;
  }
  
  .header-actions .btn {
    width: 100%;
    margin-right: 0 !important;
  }
}
</style>
